export const GENERAL_STATUS = {
    0: "Pendiente",
    1: "Completado",
    2: "Pago finalizado",
    3: "Pago pendiente",
    4: "Envio pendiente",
    5: "Retirado",
    6: "Retiro pendiente",
    7: "Pago y envio/retiro pendiente",
    8: "Entregado",
    9: "Cancelado",
    10: "En preparación",
    11: "Cambiado"
}

// Estados de la comanda de cafetería (kitchen_order.status). Se muestran en el
// listado de ventas para filas de canal cafetería, en lugar del estado genérico.
export const COMANDA_STATUS = {
    pending: "Comanda pendiente",
    ready: "Lista para entregar",
    delivered: "Entregada",
    cancelled: "Cancelada"
}

export const TICKET_STATUS = {
    2: "Pago finalizado",
    3: "Pago pendiente",
    9: "Cancelado"
}

export const TICKET_PAYMENT_METHOD = {
    1: "Efectivo",
    2: "Transferencia",
    3: "Mercado Pago",
    4: "Mercado Libre",    
    5: "POS",
    // docs/spec-cuenta-empleados.md: la venta se saldó contra la cuenta corriente del empleado.
    // No representa plata que se haya movido — ningún balde del arqueo lo reclama.
    6: "Cuenta de empleado",
    // docs/spec-eventos-consumo-incluido.md: la comanda se registró como incluida en la entrada de
    // un evento. Tampoco es plata: el ticket es de $0 y no entra a la caja ni se factura.
    7: "Incluido en evento",
    // backend docs/spec-premios-cortesias-vouchers.md: lo entregado lo pagó un voucher de premio o
    // cortesía (producto en cafetería o mesa para N). Ticket de $0, sin caja ni factura.
    8: "Premio o cortesía"
}

// Medios que este mapa NOMBRA pero que NO se pueden ELEGIR (spec-cuenta-empleados TC-017;
// spec-eventos-consumo-incluido TC-043).
//
// Dos pantallas arman su `<select>` **iterando** `TICKET_PAYMENT_METHOD`: la edición de un ticket
// (`TableTicketComponent`) y el descuento por medio de pago (`GeneralSettingsComponent`). Con sólo
// agregar la clave de arriba —que hace falta para que la grilla de Ventas muestre un texto en vez
// de un número pelado— las dos lo ofrecerían solas:
//   · la edición ofrecería mover un ticket a «Cuenta de empleado», que el backend rechaza con
//     3607 porque crearía una deuda que el libro nunca registró;
//   · la configuración dejaría asignarle un descuento por medio de pago al consumo a cuenta, que
//     no tiene ningún sentido y descontaría en silencio.
//
// Lo mismo vale para «Incluido en evento» (spec-eventos-consumo-incluido FR-117, TC-043): un
// ticket sólo llega a ese medio desde el POS de cafetería, contra una mesa de un evento que
// declara qué incluye. Editarlo, o llevar otro ticket ahí, lo rechaza el backend con 7605.
//
// Vive acá y no repetido en cada componente: si mañana aparece un tercer consumidor del mapa, es
// una línea y no un olvido.
// El 8 (premio o cortesía) igual: sólo lo escribe el canje de un voucher; el backend rechaza
// llevar un ticket ahí o sacarlo de ahí (3321).
export const HIDDEN_PAYMENT_METHODS = [6, 7, 8]

// Las opciones ELEGIBLES del mapa, para armar un `<select>`. Devuelve [[code, label], …].
export const selectablePaymentMethods = () =>
    Object.entries(TICKET_PAYMENT_METHOD).filter(([code]) => !HIDDEN_PAYMENT_METHODS.includes(Number(code)))

export const SHIPPING_TYPE = {
    1: "Envio a domicilio",
    2: "Retiro en local",
    3: "Envio por MercadoLibre"
}

// Versión corta para la columna "Envío" del listado de Ventas: la etiqueta larga no entra
// en el ancho de la celda y la columna se lee de un vistazo.
export const SHIPPING_TYPE_SHORT = {
    1: "Envío",
    2: "Retiro",
    3: "Envío ML"
}

export const SHIPPING_STATUS = {
    4: "Envio pendiente",
    5: "Retirado",
    6: "Retiro pendiente",
    8: "Entregado",
    9: "Cancelado"
}

export const PRODUCT_TYPE = {
    1: "Producto",
    2: "Accesorio"
}

// Cortas a propósito. Las dos columnas que las usan ya se titulan "Estado" o "Estado de pago",
// y el documento es una factura o una reposición: repetir "Pago" en el valor era redundante y
// hacía que la celda se partiera en dos líneas, engordando toda la fila.
//
// De paso arregla el buscador por etiqueta: 'Pago finalizado' y 'Pago pendiente' compartían el
// prefijo "Pago", así que teclearlo matcheaba las DOS y `buildEnumLabelFilter` descartaba la
// rama entera (ver el comentario en backend/src/utils/enumSearch.js). 'Pagada' y 'Pendiente' no
// comparten prefijo. OJO: estas etiquetas tienen que seguir espejando las del backend, que son
// contra las que se busca — si cambian acá, cambian allá.
export const RESTOCKING_PAYMENT_STATUS = {
    1: "Pagada",
    2: "Pendiente",
}

// ENI-32: las facturas de proveedor reusan el enum de arriba a propósito — es el mismo estado
// de pago, con las etiquetas y los colores que el usuario ya conoce de las reposiciones. Lo que
// cambia es DÓNDE vive: en la factura, y la reposición lo hereda.
export const PROVIDER_INVOICE_TYPES = [
    { value: 'purchase', label: 'Compra' },
    { value: 'consignment_settlement', label: 'Liquidación de consignados' },
]

// Etiqueta CORTA, para la grilla y el detalle. La larga vive en PROVIDER_INVOICE_TYPES, que es
// el selector del alta: ahí conviene ser explícito porque el usuario está eligiendo, pero en una
// columna titulada "Tipo" "Liquidación de consignados" partía la celda en tres líneas.
export const PROVIDER_INVOICE_TYPE_LABELS = {
    purchase: 'Compra',
    consignment_settlement: 'Liquidación',
}

// Moneda de las facturas de proveedor. Los códigos son los MISMOS que usan los movimientos de
// caja y las cuentas (`movements_enum.currency` en el backend): 1 = pesos, 2 = dólares. No
// inventar un enum propio acá — el pago de una factura termina en un movimiento, y si los
// códigos no coincidieran habría que traducir en el medio.
export const CURRENCIES = [
    { value: 1, label: 'Pesos ($)', symbol: '$' },
    { value: 2, label: 'Dólares (USD)', symbol: 'USD' },
]

// El símbolo que precede a un importe. Default a '$' y no a vacío: un importe sin símbolo se lee
// como pesos igual, así que el default tiene que ser el caso mayoritario.
export function currencySymbol(currency) {
    const encontrada = CURRENCIES.find((c) => Number(c.value) === Number(currency));
    return encontrada ? encontrada.symbol : '$';
}

// ENI-92: categorías de proveedor. 'general' = sin clasificar (default); la reposición de
// insumos muestra solo cafetería + general. El orden define cómo aparecen en el selector.
export const PROVIDER_CATEGORIES = [
    { value: 'cafeteria', label: 'Cafetería' },
    { value: 'juegos', label: 'Juegos' },
    { value: 'limpieza', label: 'Limpieza' },
    { value: 'general', label: 'General' },
]

export const MOVEMENTS_CURRENCY = {
    1: "$",
    2: "USD",
}

export const SPEND_TYPE = {
    1: "Fijo",
    2: "Variable",
}

export const TRANSACTION_TYPE = {
    1: "Venta",
    2: "Gasto",
    3: "Transferencia",
    4: "Deposito",
}

export const INVOICE_TYPE = {
    101: "eTicket",
    111: "eFactura",
    102: "NC eTicket",
    112: "NC eFactura",
}

export const TICKET_INVOICE_STATUS = {
    0: "PENDIENTE",
    1: "FACTURADO",
    2: "IGNORADO",
}

export const ROL_TYPE = {
    "ROLE_ADMIN": 1,
    "ROLE_MANAGER": 2,
    "ROLE_USER": 3,
    "ROLE_FINANCE": 4,
}

export function isAdmin(role) {
    return role === ROL_TYPE.ROLE_ADMIN
}

export function isAdminOrManager(role) {
    return role == ROL_TYPE.ROLE_ADMIN || role == ROL_TYPE.ROLE_MANAGER
}

export function isAdminOrFinance(role) {
    return role == ROL_TYPE.ROLE_ADMIN || role == ROL_TYPE.ROLE_FINANCE
}
